import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { getInset, Input, InputSize, CalendarIcon } from '@lifechurch/react-ion';
import { Calendar } from '../Calendar';
import { useDayjsLocale, useOnClickOutside } from '@churchonline/hooks';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
const Wrapper = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
`;
const InputWrapper = styled.div `
  position: relative;
  ${(props) => {
    if (props.width) {
        return `width: ${props.width}px`;
    }
    else {
        return null;
    }
}};
  input {
    background-color: transparent;

    &:not(:disabled):hover {
      cursor: pointer;
    }
  }
`;
const IconWrapper = styled.div `
  position: absolute;
  pointer-events: none;
  top: ${(props) => {
    if (props.size === InputSize.SM) {
        if (props.hasLabel) {
            return '24px';
        }
        else {
            return '8px';
        }
    }
    else if (props.size === InputSize.LG) {
        if (props.hasLabel) {
            return '41px';
        }
        else {
            return '20px';
        }
    }
    else {
        if (props.hasLabel) {
            return '28px';
        }
        else {
            return '12px';
        }
    }
}};
  ${getInset('insetInlineEnd', '10px')};
`;
const CalendarWrapper = styled.span `
  position: absolute;
  z-index: 5;
  background-color: #ffffff;
  display: ${(props) => (props.focused ? 'inline-block' : 'none')};
  width: ${(props) => (props.range ? '496px' : '256px')};
  top: ${(props) => {
    if (props.size === InputSize.SM) {
        if (props.hasLabel) {
            return '54px';
        }
        else {
            return '34px';
        }
    }
    else if (props.size === InputSize.MD) {
        if (props.hasLabel) {
            return '62px';
        }
        else {
            return '42px';
        }
    }
    else {
        if (props.hasLabel) {
            return '82px';
        }
        else {
            return '58px';
        }
    }
}};
`;
export const SingleDatePicker = ({ fromMonth = new Date(), size = InputSize.MD, selectedDay, disabled, label, width, locale = 'en', ...props }) => {
    const [text, setText] = useState('');
    const [state, setState] = useState({
        focused: false,
    });
    const toggleCalendar = () => {
        setState({
            focused: !state.focused,
        });
    };
    const closeCalendar = () => {
        setState({
            focused: false,
        });
    };
    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    const { onDayClick, onDayMouseEnter, ...inputProps } = props;
    useOnClickOutside([calendarRef, inputRef], () => closeCalendar());
    const { formattedLocale, isLocaleLoaded, loadedLocales } = useDayjsLocale(locale);
    useEffect(() => {
        if (selectedDay && isLocaleLoaded) {
            setText(dayjs(selectedDay).format('dddd l'));
        }
    }, [selectedDay, formattedLocale, isLocaleLoaded]);
    if (!loadedLocales[formattedLocale] && isLocaleLoaded) {
        return null;
    }
    // @ts-ignore
    return (React.createElement(Wrapper, null,
        React.createElement(InputWrapper, { disabled: disabled, width: width, ref: inputRef },
            React.createElement(Input, { onClick: toggleCalendar, readOnly: true, focused: state.focused, value: text, size: size, disabled: disabled, label: label, ...inputProps, "data-testid": 'datepicker-input' }),
            React.createElement(IconWrapper, { size: size, hasLabel: !!label },
                React.createElement(CalendarIcon, null))),
        React.createElement(CalendarWrapper, { focused: state.focused, hasLabel: !!label, size: size, "data-testid": 'datepicker-calendar', range: false, ref: calendarRef },
            React.createElement(Calendar, { fromMonth: fromMonth, selectedDays: selectedDay, weekdaysShort: dayjs.weekdaysMin(), ...props }))));
};
export const RangeDatePicker = ({ fromMonth = new Date(), size = InputSize.MD, disabled, from, enteredTo, label, width, autoDismiss = false, locale = 'en', ...props }) => {
    const [focused, setFocused] = useState(false);
    const [to, setTo] = useState(enteredTo);
    const [text, setText] = useState('');
    const calendarRef = useRef(null);
    const inputRef = useRef(null);
    const { onDayClick, onDayMouseEnter, ...inputProps } = props;
    const toggleCalendar = () => {
        setFocused(!focused);
    };
    const closeCalendar = () => {
        setFocused(false);
    };
    const confirmSecondDateSelection = () => {
        if (from !== enteredTo) {
            setTo(enteredTo);
        }
    };
    const contextAwareOnDayClick = (day, modifiers, e) => {
        confirmSecondDateSelection();
        if (onDayClick) {
            onDayClick(day, modifiers, e);
        }
    };
    useOnClickOutside([calendarRef, inputRef], () => closeCalendar());
    const { formattedLocale, isLocaleLoaded, loadedLocales } = useDayjsLocale(locale);
    useEffect(() => {
        if (autoDismiss && from && to) {
            closeCalendar();
        }
    }, [to]);
    useEffect(() => {
        if (from && isLocaleLoaded) {
            const formattedFrom = dayjs(from).format('dddd l');
            const formattedTo = dayjs(enteredTo).format('dddd l');
            setText(enteredTo ? `${formattedFrom} – ${formattedTo}` : `${formattedFrom} –`);
        }
    }, [enteredTo, from, formattedLocale, isLocaleLoaded]);
    if (!loadedLocales[formattedLocale] && isLocaleLoaded) {
        return null;
    }
    return (React.createElement(Wrapper, null,
        React.createElement(InputWrapper, { disabled: disabled, width: width, ref: inputRef },
            React.createElement(Input, { onClick: toggleCalendar, readOnly: true, focused: focused, value: text, size: size, disabled: disabled, label: label, ...inputProps }),
            React.createElement(IconWrapper, { size: size, hasLabel: !!label },
                React.createElement(CalendarIcon, null))),
        React.createElement(CalendarWrapper, { focused: focused, hasLabel: !!label, size: size, "data-testid": 'datepicker-calendar', range: true, ref: calendarRef },
            React.createElement(Calendar, { fromMonth: fromMonth, className: 'Range', numberOfMonths: 2, modifiers: { start: from, end: enteredTo }, selectedDays: from && enteredTo ? [from, { from: from, to: enteredTo }] : [], zIndex: 5, weekdaysShort: dayjs.weekdaysMin(), ...props, onDayClick: contextAwareOnDayClick }))));
};
